import { useState, useEffect, useRef } from "react"
import { Input, Button, Card, CardBody, Spinner, Badge } from "reactstrap"
import {
  formatCustomerTypeLabel,
  getCustomerOrganizationText,
} from "helpers/customerOnboarding"
import "./CustomerSearch.css"

const CustomerSearch = ({
  onCustomerSelect,
  placeholder = "Search customers by name, email, phone...",
  apiSearchFunction,
  minSearchLength = 2,
  className = "",
  isSearching = false,
}) => {
  const [searchTerm, setSearchTerm] = useState("")
  const [suggestions, setSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const searchRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = event => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  useEffect(() => {
    if (searchTerm.length >= minSearchLength) {
      const timer = setTimeout(async () => {
        try {
          const results = await apiSearchFunction(searchTerm)
          setSuggestions(results?.data ?? [])
          setShowSuggestions(true)
        } catch (error) {
          console.error("Search failed:", error)
          setSuggestions([])
        }
      }, 300)

      return () => clearTimeout(timer)
    }

    setSuggestions([])
    setShowSuggestions(false)
  }, [searchTerm, apiSearchFunction, minSearchLength])

  const handleCustomerSelect = customer => {
    onCustomerSelect(customer)
    setSearchTerm("")
    setShowSuggestions(false)
    setSuggestions([])
  }

  return (
    <div className={`customer-search position-relative ${className}`} ref={searchRef}>
      <div className="position-relative">
        <Input
          type="text"
          placeholder={placeholder}
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="form-control-sm"
          disabled={isSearching}
        />
        {isSearching && (
          <Spinner
            size="sm"
            className="position-absolute"
            style={{ right: 10, top: "50%", transform: "translateY(-50%)" }}
          />
        )}
      </div>
      {isSearching && (
        <small className="text-muted d-flex align-items-center gap-1 mt-1">
          <Spinner size="sm" type="grow" />
          Searching customers...
        </small>
      )}

      {showSuggestions && suggestions.length > 0 && (
        <Card className="position-absolute w-100 mt-1 shadow" style={{ zIndex: 1000 }}>
          <CardBody className="p-2">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <small className="text-muted">Found {suggestions.length} customer(s)</small>
              <Button
                color="link"
                size="sm"
                className="p-0 text-danger"
                onClick={() => setShowSuggestions(false)}
              >
                <i className="bx bx-x"></i>
              </Button>
            </div>

            {suggestions.map(customer => {
              const organization = getCustomerOrganizationText(customer)

              return (
                <div
                  key={customer.id}
                  className="customer-suggestion p-2 border-bottom cursor-pointer hover-bg"
                  onClick={() => handleCustomerSelect(customer)}
                >
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="fw-bold">{customer.name}</div>
                    <Badge color="light" className="text-muted fs-11">
                      {formatCustomerTypeLabel(customer.customer_type) || "Customer"}
                    </Badge>
                  </div>
                  {organization && <div className="small text-primary">{organization}</div>}
                  <div className="small text-muted">
                    Phone: {customer.mobile || "-"}
                    {customer.email ? ` | Email: ${customer.email}` : ""}
                  </div>
                  {customer.full_address && (
                    <div className="small text-muted mt-1">{customer.full_address}</div>
                  )}
                </div>
              )
            })}
          </CardBody>
        </Card>
      )}

      {showSuggestions &&
        searchTerm.length >= minSearchLength &&
        suggestions.length === 0 &&
        !isSearching && (
          <Card className="position-absolute w-100 mt-1">
            <CardBody className="p-3 text-center text-muted">
              <i className="bx bx-user-x fs-4 mb-2 d-block"></i>
              No customers found
              <div className="small mt-1">Try different search terms</div>
            </CardBody>
          </Card>
        )}
    </div>
  )
}

export default CustomerSearch
